/**
 * Meu passaporte: um carimbo por lugar visitado (atividade concluída com
 * lugar em que eu estava). Grade de carimbos, contagem, e "Compartilhar"
 * gera a imagem no aparelho.
 */
import { featureEnabled } from '@nv/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../auth/store';
import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { EmptyState, ErrorState, ScreenHeader, Skeleton } from '../../ui/primitives';
import { type Carimbo, usePassaporte } from './api';
import { imagemDoPassaporte } from './imagem';

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const primeiro = (n: string) => n.trim().split(/\s+/)[0] ?? n;

export function quemEstava(c: Carimbo): string {
  if (c.everyone) return 'todo mundo';
  if (c.with.length === 0) return 'só você';
  const nomes = c.with.map(primeiro);
  if (nomes.length <= 2) return `com ${nomes.join(' e ')}`;
  return `com ${nomes.slice(0, 2).join(', ')} e mais ${nomes.length - 2}`;
}

export function PassaporteScreen() {
  const navegar = useNavigate();
  const { trip, tripId } = useTrip();
  const user = useAuthStore((s) => s.user);
  const ligado = featureEnabled(trip.features, 'carimbos');
  const passaporte = usePassaporte(tripId, ligado);
  const [gerando, setGerando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  if (!ligado) {
    return (
      <section className="screen">
        <EmptyState ilustracao="passaporte" title="Carimbos desligados" description="Quem administra a viagem desligou esta funcionalidade." />
      </section>
    );
  }
  const p = passaporte.data;

  async function compartilhar() {
    if (!p) return;
    setGerando(true);
    setAviso(null);
    try {
      const blob = await imagemDoPassaporte({ nome: user?.name ?? 'Eu', viagem: trip.name, carimbos: p.stamps, lugares: p.places, dias: p.days });
      const file = new File([blob], 'meu-passaporte.png', { type: 'image/png' });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (nav.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: 'Meu passaporte' });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'meu-passaporte.png';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        setAviso('Imagem gerada — está nos seus downloads.');
      }
    } catch (e) {
      if (!(e instanceof Error && e.name === 'AbortError')) setAviso(e instanceof Error ? e.message : 'Não foi possível gerar a imagem.');
    } finally {
      setGerando(false);
    }
  }

  return (
    <section className="screen pass" data-testid="passaporte">
      <ScreenHeader title="Meu passaporte" />

      {passaporte.isLoading && <Skeleton height={150} count={2} />}
      {passaporte.isError && <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void passaporte.refetch()} />}

      {p && p.stamps.length === 0 && (
        <EmptyState
          ilustracao="passaporte"
          title="Nenhum carimbo ainda"
          description="Cada atividade concluída com lugar vira um carimbo aqui — no seu passaporte e no de quem estava junto."
          action={{ label: 'Ir ao roteiro', onClick: () => navegar('/roteiro') }}
        />
      )}

      {p && p.stamps.length > 0 && (
        <>
          <div className="pass__grade">
            {p.stamps.map((c, i) => (
              <button type="button" className="pass__carimbo" style={{ transform: `rotate(${((i % 5) - 2) * 2}deg)` }} key={c.activityId} onClick={() => navegar(`/roteiro/${c.date}/${c.activityId}`)} data-testid="carimbo">
                <span className="pass__anel">
                  <span className="pass__lugar">{c.place}</span>
                  <span className="pass__data mono">{ddmm(c.date)}</span>
                </span>
                <span className="pass__quem">{quemEstava(c)}</span>
              </button>
            ))}
          </div>
          {aviso && <p className="auth-note" role="status">{aviso}</p>}
          <button type="button" className="nv-btn nv-btn--primary nv-btn--block" disabled={gerando} onClick={() => void compartilhar()} data-testid="compartilhar-passaporte">
            <Icon name="compartilhar" size={18} /> {gerando ? 'Gerando a imagem…' : 'Compartilhar meu passaporte'}
          </button>
        </>
      )}
    </section>
  );
}
